import * as yup from "yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import { tinFormat } from "../../../utils/stringUtils";

import Textbox from "../../../components/common/Textbox";
import TextboxMask from "./../../../components/common/TextboxMask";
import ReactSelect from "./../../../components/common/ReactSelect";
import Datepicker from "../../../components/common/Datepicker";
import Toaster from "../../../components/common/Toaster";

import journalService from "../../../services/journalService";

const schema = yup.object({
  name: yup.string().required("Please enter name."),
  type: yup.object().shape({
    label: yup.string().required("Please select client type."),
    value: yup.string().required("Please select client type."),
  }),
});

const types = [
  { label: "Customer", value: 1 },
  { label: "Supplier", value: 2 },
];

const JournalClientForm = ({ isLoading, setLoading, onSuccess }) => {
  const [showFields, setShowFields] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const handleCheckboxChange = () => setShowFields(!showFields);

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const item = { ...info };
      item["type"] = info.type.value;
      item["tin"] = info.tin !== undefined ? info.tin : null;
      item["identification_date_issued"] =
        info.identification_date_issued !== undefined
          ? info.identification_date_issued
          : null;

      item["identification_date_expiry"] =
        info.identification_date_expiry !== undefined
          ? info.identification_date_expiry
          : null;

      await journalService.saveJournalClientAsync(item);

      setLoading(false);
      Toaster.Success("Client Saved Successfully");
      onSuccess();
    } catch (ex) {
      setLoading(false);
      Toaster.Error("Something went wrong.");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="name-textbox"
            name="name"
            type="text"
            title="Name"
            register={register("name")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="address-textbox"
            name="address"
            type="text"
            title="Address"
            register={register("address")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <TextboxMask
            id="tin-textbox"
            name="tin"
            type="text"
            title="TIN"
            format={tinFormat}
            setValue={setValue}
            register={register("tin")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="type-dropdown"
            name="type"
            title="Type"
            options={types}
            control={control}
            register={register("type")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <input
            id="scpwd-checkbox"
            className="text-blue-500 cursor-pointer border-gray-300 rounded"
            type="checkbox"
            onChange={handleCheckboxChange}
          />
          <label className="ml-3 text-sm text-gray-400">
            Senior Citizen / PWD?
          </label>
        </div>
      </div>
      {showFields && (
        <>
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <Textbox
                id="identification-number-textbox"
                name="identification_number"
                type="text"
                title="ID No."
                register={register("identification_number")}
                errors={errors}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <Datepicker
                id="identification-date-issued"
                name="identification_date_issued"
                title="Date Issued"
                dateFormat="MM-DD-yyyy"
                timeFormat={false}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register("identification_date_issued")}
                errors={errors}
              />
            </div>
            <div className="col-span-1">
              <Datepicker
                id="identification-date-expiry"
                name="identification_date_expiry"
                title="Expiry Date"
                dateFormat="MM-DD-yyyy"
                timeFormat={false}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register("identification_date_expiry")}
                errors={errors}
              />
            </div>
          </div>
        </>
      )}
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default JournalClientForm;
